import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"

// ==========================================
// Types
// ==========================================

export interface ProposalItem {
  id: string
  proposal_id: string
  catalog_item_id: string
  price_table_item_id?: string
  item_name: string
  item_type: string
  billing_type: string
  billing_cycle?: string
  quantity: number
  unit_price: number
  discount_percent: number
  subtotal: number
  recurring_months?: number
  recurring_total?: number
  sort_order: number
  notes?: string
}

export interface Approval {
  id: string
  proposal_id: string
  level: number
  approver_id?: string
  approver_role: string
  status: string
  comments?: string
  decided_at?: string
  approver_name?: string
}

export interface CommercialConditions {
  payment_term?: string | null
  contract_period?: string | null
  payment_method?: string | null
  adjustment_index?: string | null
  early_termination_fee_percent?: number | null
}

export interface ScopeModule {
  icon: string
  name: string
  description: string
}

export interface Proposal {
  id: string
  opportunity_id: string
  version: number
  lead_id?: string
  price_table_id?: string
  list_price: number
  discount_percent: number
  final_price: number
  one_time_total: number
  recurring_monthly_total: number
  status: string
  current_approval_level: number
  required_approval_level: number
  valid_until: string
  terms?: string
  notes?: string
  pdf_url?: string
  payment_term?: string | null
  contract_period?: string | null
  payment_method?: string | null
  adjustment_index?: string | null
  early_termination_fee_percent?: number | null
  scope_text?: string | null
  scope_filename?: string | null
  scope_modules?: ScopeModule[] | null
  scope_assessment_submission_ids?: string[] | null
  scope_free_text?: string | null
  company_snapshot?: Record<string, any> | null
  created_by: string
  created_at: string
  updated_at: string
  proposal_items: ProposalItem[]
  approvals: Approval[]
  opportunity_name?: string
  lead_company_name?: string
  creator_name?: string
}

export interface ProposalList {
  items: Proposal[]
  total: number
  page: number
  per_page: number
  pages: number
}

export type ProposalStatus =
  | "draft"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "sent"
  | "accepted"
  | "declined"
  | "expired"

// ==========================================
// Query Hooks
// ==========================================

interface UseProposalsParams {
  page?: number
  perPage?: number
  search?: string
  status?: ProposalStatus | null
  opportunityId?: string | null
  createdBy?: string | null
}

export function useProposals(params: UseProposalsParams = {}) {
  return useQuery({
    queryKey: ["proposals", params],
    queryFn: async () => {
      const query: Record<string, string | number> = {
        page: params.page || 1,
        per_page: params.perPage || 20,
      }
      if (params.search) query.search = params.search
      if (params.status) query.status = params.status
      if (params.opportunityId) query.opportunity_id = params.opportunityId
      if (params.createdBy) query.created_by = params.createdBy
      const { data } = await api.get<ProposalList>("/v1/proposals/", { params: query })
      return data
    },
  })
}

export function useProposal(proposalId: string | null) {
  return useQuery({
    queryKey: ["proposals", proposalId],
    queryFn: async () => {
      const { data } = await api.get<Proposal>(`/v1/proposals/${proposalId}`)
      return data
    },
    enabled: !!proposalId,
  })
}

// ==========================================
// Mutation Hooks
// ==========================================

export function useCreateProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (proposalData: Record<string, unknown>) => {
      const { data } = await api.post<Proposal>("/v1/proposals/", proposalData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals"] }),
  })
}

export function useUpdateProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, data: proposalData }: { proposalId: string; data: Record<string, unknown> }) => {
      const { data } = await api.put<Proposal>(`/v1/proposals/${proposalId}`, proposalData)
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["proposals"] })
      qc.invalidateQueries({ queryKey: ["proposals", data.id] })
    },
  })
}

export function useDeleteProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (proposalId: string) => {
      await api.delete(`/v1/proposals/${proposalId}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals"] }),
  })
}

export function useSubmitProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const { data } = await api.post<Proposal>(`/v1/proposals/${proposalId}/submit`)
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["proposals"] })
      qc.invalidateQueries({ queryKey: ["proposals", data.id] })
    },
  })
}

export function useApproveProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, comments }: { proposalId: string; comments?: string }) => {
      const { data } = await api.post<Proposal>(`/v1/proposals/${proposalId}/approve`, { comments })
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["proposals"] })
      qc.invalidateQueries({ queryKey: ["proposals", data.id] })
    },
  })
}

export function useRejectProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, comments }: { proposalId: string; comments?: string }) => {
      const { data } = await api.post<Proposal>(`/v1/proposals/${proposalId}/reject`, { comments })
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["proposals"] })
      qc.invalidateQueries({ queryKey: ["proposals", data.id] })
    },
  })
}

export function useSendProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const { data } = await api.post<Proposal>(`/v1/proposals/${proposalId}/send`)
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["proposals"] })
      qc.invalidateQueries({ queryKey: ["proposals", data.id] })
    },
  })
}

export function useDuplicateProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const { data } = await api.post<Proposal>(`/v1/proposals/${proposalId}/duplicate`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals"] }),
  })
}

// ==========================================
// Sharing & Tracking Hooks
// ==========================================

import type { ProposalShareRequest, ProposalShareResponse, ProposalTracking } from "@/types"

export function useShareProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, data }: { proposalId: string; data: ProposalShareRequest }) => {
      const { data: resp } = await api.post<ProposalShareResponse>(`/v1/proposals/${proposalId}/share`, data)
      return resp
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proposals"] })
    },
  })
}

export function useProposalTracking(proposalId: string | null) {
  return useQuery({
    queryKey: ["proposal-tracking", proposalId],
    queryFn: async () => {
      const { data } = await api.get<ProposalTracking[]>(`/v1/proposals/${proposalId}/tracking`)
      return data
    },
    enabled: !!proposalId,
    refetchInterval: 15000,
  })
}

export function useRevokeLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, linkId }: { proposalId: string; linkId: string }) => {
      await api.delete(`/v1/proposals/${proposalId}/links/${linkId}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proposal-tracking"] })
    },
  })
}

// Upload de escopo + analise IA. Retorna {ok, modules, modules_count, text_preview, filename}
export interface ScopeParseResponse {
  ok: boolean
  filename: string
  modules_count: number
  modules: ScopeModule[]
  text_preview: string
}

export function useUploadProposalScope() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, file }: { proposalId: string; file: File }) => {
      const fd = new FormData()
      fd.append("file", file)
      const { data } = await api.post<ScopeParseResponse>(
        `/v1/proposals/${proposalId}/scope/parse`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      )
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["proposal", vars.proposalId] })
      qc.invalidateQueries({ queryKey: ["proposals"] })
    },
  })
}

// Versao URL: backend faz fetch + extracao + Claude. Suporta HTML/PDF/TXT.
export interface ScopeParseUrlResponse extends ScopeParseResponse {
  source_url?: string
  content_type?: string
}

export function useUploadProposalScopeUrl() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, url }: { proposalId: string; url: string }) => {
      const { data } = await api.post<ScopeParseUrlResponse>(
        `/v1/proposals/${proposalId}/scope/parse-url`,
        { url },
        { timeout: 120000 }
      )
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["proposal", vars.proposalId] })
      qc.invalidateQueries({ queryKey: ["proposals"] })
    },
  })
}

// Parse de texto livre (textarea no wizard) via IA — não persiste; o wizard
// combina com as outras fontes via set-modules.
export function useUploadProposalScopeText() {
  return useMutation({
    mutationFn: async ({ proposalId, text }: { proposalId: string; text: string }) => {
      const { data } = await api.post<ScopeParseResponse>(
        `/v1/proposals/${proposalId}/scope/parse-text`,
        { text },
        { timeout: 120000 }
      )
      return data
    },
  })
}

// Substitui em massa todos os items de uma proposta (usado pelo wizard de edicao).
// Apaga existentes e recria conforme payload, recomputando totais.
export function useReplaceProposalItems() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, items }: { proposalId: string; items: any[] }) => {
      const { data } = await api.post(`/v1/proposals/${proposalId}/items/replace`, { items })
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["proposal", vars.proposalId] })
      qc.invalidateQueries({ queryKey: ["proposals"] })
      qc.invalidateQueries({ queryKey: ["opportunities"] })
    },
  })
}

// Busca o HTML renderizado da proposta (auth) e retorna URL de blob para abrir
// em nova aba. Caller deve chamar URL.revokeObjectURL apos uso para liberar memoria.
export async function fetchProposalPreviewBlob(proposalId: string): Promise<string> {
  const response = await api.get(`/v1/proposals/${proposalId}/render`, { responseType: "blob" })
  return URL.createObjectURL(response.data)
}

// ==========================================
// Escopo derivado de Assessments
// ==========================================

export interface LeadAssessmentItem {
  id: string
  pillar: string
  assessment_slug: string
  assessment_name: string
  company_name: string
  contact_name: string
  contact_email: string
  submitted_at: string | null
  created_at: string
  total_percentage: number
  maturity_band: string | null
  has_directories: boolean
  has_targets: boolean
  directories_count: number
  targets_count: number
}

export function useLeadAssessments(leadId: string | null | undefined) {
  return useQuery({
    queryKey: ["lead-assessments", leadId],
    queryFn: async () => {
      const { data } = await api.get<LeadAssessmentItem[]>(
        `/v1/proposals/leads/${leadId}/assessments`,
      )
      return data
    },
    enabled: !!leadId,
    staleTime: 30_000,
  })
}

export interface BuildScopeFromAssessmentsResponse {
  modules: ScopeModule[]
  directories_count: number
  targets_count: number
}

export function useBuildScopeFromAssessments() {
  return useMutation({
    mutationFn: async ({ submissionIds }: { submissionIds: string[] }) => {
      const { data } = await api.post<BuildScopeFromAssessmentsResponse>(
        `/v1/proposals/scope/from-assessments`,
        { submission_ids: submissionIds },
      )
      return data
    },
  })
}

export function useApplyScopeFromAssessments() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, submissionIds }: { proposalId: string; submissionIds: string[] }) => {
      const { data } = await api.post(
        `/v1/proposals/${proposalId}/scope/apply-assessments`,
        { submission_ids: submissionIds },
      )
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["proposal", vars.proposalId] })
      qc.invalidateQueries({ queryKey: ["proposals"] })
    },
  })
}

// Persiste a combinação final de scope_modules (orquestrada pelo wizard
// quando o usuário escolheu múltiplas fontes: assessments + arquivo + URL).
export function useSetProposalScopeModules() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      proposalId, modules, scopeText, scopeFilename, submissionIds, freeText,
    }: { proposalId: string; modules: ScopeModule[]; scopeText?: string; scopeFilename?: string; submissionIds?: string[]; freeText?: string }) => {
      const { data } = await api.post(
        `/v1/proposals/${proposalId}/scope/set-modules`,
        {
          modules,
          scope_text: scopeText,
          scope_filename: scopeFilename,
          submission_ids: submissionIds,
          free_text: freeText,
        },
      )
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["proposal", vars.proposalId] })
      qc.invalidateQueries({ queryKey: ["proposals"] })
    },
  })
}
